import { defineDirective, FieldViewModel, selectMany } from "../../../../omnia"
import { IFieldsService, INutrient, INutritionService } from "../../../../types"
import { DashboardSelection } from "../../selection"
import { ICroppingApplication, ICroppingPlan, IDashboardContext } from "../../types"
import { PlanMapView } from "../../view/plan"
import { AppZoneVm } from "./appZone"

export class AppZoneEditorCtrl {

    static create(): ng.IDirective {
        return defineDirective(AppZoneEditorCtrl, "app/nutrition/dashboard/map/app/edit/edit.html", {
            context: "="
        });
    }

    private form: ng.IFormController;

    public context: IDashboardContext;

    public cropping: FieldViewModel;
    public croppingId: number;

    public plan: ICroppingPlan;
    public planId: number;

    public app: ICroppingApplication;

    private _changes: {
        croppingId: number;
        appId: number;
        zones: AppZoneVm[];
    }[] = [];

    public zones: AppZoneVm[];
    public nutrients: INutrient[];


    static $inject = ["$scope", "OmniaNutritionService", "OmniaFieldsService"];

    constructor(private $scope: ng.IScope, private svc: INutritionService, private fieldSvc: IFieldsService) {
        $scope.$watch("vm.context", () => this.setupComponent());
    }

    get limeUnits() {
        return "kg/ha"
    }

    get selection(): DashboardSelection {
        if (this.context) {
            return this.context.selection;
        }
    }

    set selection(value: DashboardSelection) {
        this.context.setSelection(value, "AppZoneEditorCtrl/setter");
    }

    protected setupComponent() {

        let ctxt = this.context;

        if (!ctxt) {
            return;
        }

        this.$scope.$watch("vm.selection", () => {

            if (this.hasSelectionChanged()) {
                this.setupZones();
                this.setupNutrients();
            }

        });

    }

    private hasSelectionChanged(): boolean {

        let s = this.context.selection;
        let app = this.app;

        if (!s) return true;
        if (!app) return true;

        if (((s.croppingId != this.croppingId) || (s.appId != app.appId))) {
            return true;
        }

        return false;

    }

    private setupNutrients() {

        this.nutrients = this.context.nutrients.nutrients.filter(n => {
            return this.zones.some(s => s.contributesNutrient(n));
        })

    }
    private setupZones() {

        let s = this.context.selection;

        this.cropping = this.fieldSvc.findCropping(s.croppingId);
        this.plan = this.context.planReader.getCroppingPlan(s.croppingId, s.planId);
        this.app = this.plan.applications.find(a => a.appId == s.appId);

        this.planId = s.planId;
        this.croppingId = s.croppingId;

        if (!this.restoreZones()) {
            this.zones = AppZoneVm.createZones(this.app.zones, this.app.app.ProductUnit, z => this.updateOnZoneChanged(z));
            this.storeZones();
        }

    }

    private restoreZones(): boolean {

        if (this.cropping && this.app) {

            let chg = this._changes.find(c => (c.croppingId == this.croppingId) && (c.appId == this.app.appId));

            if (chg) {
                this.zones = chg.zones;
                return true;
            }
        }

    }

    private storeZones() {

        let chg = this._changes.find(c => (c.croppingId == this.croppingId) && (c.appId == this.app.appId));

        if (!chg) {

            chg = {
                croppingId: this.croppingId,
                appId: this.app.appId,
                zones: this.zones
            }

            this._changes.push(chg);
        }

    }

    isZoneSelected(zone: AppZoneVm): boolean {
        let s = this.selection;
        return s && zone && (s.croppingId == zone.croppingId) && (s.appId == zone.appId) && (s.zoneNumber == zone.zoneNumber);
    }


    selectZone(zone: AppZoneVm) {

        if (zone && this.context) {
            let s = this.context.selection.selectZone(zone.zoneNumber);
            this.context.setSelection(s, "AppZoneEditorCtrl/selectZone");
            this.context.mapCtxt.selectZoneOnCurrentView(s.croppingId, zone.zoneNumber);
        }

    }

    private updateOnZoneChanged(zone: AppZoneVm) {

        let views = this.context.mapCtxt.views
            .filter(v => {
                if (v instanceof PlanMapView) {
                    return (v.getApplicationId() == this.app.appId);
                }
            });

        let ovr: any;

        if (zone.hasChanged()) {

            let nutrientRates = this.nutrients.reduce((ntr, n) => {
                ntr[n.key] = zone.getNutrientRate(n);
                return ntr;
            }, {});

            ovr = {
                productRate: zone.productRate,
                nutrientRates: nutrientRates
            }

        };

        views.forEach(v => {
            v.setZoneOverride(zone.id, ovr);
        });

    }

    hasChanged(): boolean {
        if (this._changes.some(c => c.zones.some(z => z.hasChanged()))) return true;
        return false;
    }

    canCommit(): boolean {

        if (!this.zones) return false;
        if (!(this.form && this.form.$valid)) return false;
        if (!this.hasChanged()) return false;

        return true;

    }

    cancelAllChanges() {
        this.zones.forEach(z => z.cancelChanges());
    }

    commit() {

        if (!this.canCommit()) return;

        let models = selectMany(this._changes.map(c => c.zones))
            .filter(z => z.hasChanged())
            .map(z => z.getUpdateModel());

        return this.svc.updateApplicationZones(models)
            .then(() => this.cancel())
            .then(() => this.context.refresh());

    }

    private cancel() {
        this.context.mapCtxt.clearZoneOverrides();
        this.context.endCurrentAction();
    }

}
